/**
 * El mostrador — la pantalla en la que el sistema abre.
 *
 * LA VENTA OCUPA TODO
 * -------------------
 * Un tablero de indicadores responde preguntas que el dueño se hace una vez por
 * dia. Esta pantalla responde las que se hace treinta veces, y la principal es
 * "cobrame esto". Por eso aca no hay nada mas que la venta.
 *
 * Hubo una columna de 288px al costado con la cotizacion, el interruptor de
 * discrecion y cuatro contadores. Estaban para avisar, no para mirarlos, y aun
 * asi se llevaban un quinto del ancho de la pantalla mas usada del sistema.
 * Viven ahora en el header (`BarraEstado`), donde ademas alcanzan a todos los
 * modulos: la cotizacion y la discrecion son globales y no tenia sentido que
 * solo se pudieran tocar desde aca.
 */
import { useCallback, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { convertir, formatear } from "@tora/shared";
import { Pagina } from "@/components/layout/Pagina";
import { Card } from "@/components/ui/card";
import { TicketVenta, type CuentaFinanciera } from "@/components/venta/TicketVenta";
import { PanelVentaCerrada, type VentaCerrada } from "@/components/venta/PanelVentaCerrada";
import { SelectorCliente } from "@/components/venta/SelectorCliente";
import { DialogLimiteCredito } from "@/components/venta/DialogLimiteCredito";
import {
  deudaEntraEnElCredito,
  leerCreditoExcedido,
  type CreditoExcedido,
} from "@/components/venta/limite-credito";
import { DialogCliente } from "@/components/clientes/DialogCliente";
import { monedaAnclaje } from "@/components/clientes/logica";
import { avisarUsadoSinPrecio, useAvisoEnTaller } from "@/components/venta/aviso-en-taller";
import { etiquetaMedio } from "@/components/cobro/logica";
import {
  encargadosDe,
  itemDeResultado,
  usadoSinPrecio,
  useTicket,
  type ResultadoParaTicket,
} from "@/lib/ticket-store";
import { useBuscador } from "@/lib/buscador-context";
import {
  useCliente,
  useCuentasFinancieras,
  useConfig,
  useEmitirVenta,
  useEmitirVentaContado,
  useCrearCliente,
} from "@/lib/queries";
import { mostrarError } from "@/lib/errores";
import { aNumero, type EntregaUsado, type PagoVentaContado } from "@/lib/tipos";
import type { LineaPagoRemito } from "@/lib/remito";

export const Route = createFileRoute("/")({ component: Mostrador });

function Mostrador(): JSX.Element {
  /**
   * El buscador ⌘K vive en el layout y sirve para CONSULTAR desde cualquier
   * pantalla. El campo de carga del ticket es otra cosa y vive adentro del
   * ticket: sirve para ARMAR la venta sin abrir nada.
   *
   * `internos` es el interruptor de discrecion: en el mostrador la pantalla la
   * ve tambien el cliente, y con esto apagado desaparecen margenes y totales.
   * El interruptor esta en el header para tenerlo a mano desde cualquier
   * pantalla, pero solo apaga esto y la caja de la barra (Ganancias, Socios y
   * Caja no lo leen); aca solo se lee el estado para pasarselo al ticket.
   */
  const { internos } = useBuscador();

  const {
    ticket,
    agregar,
    editarItem,
    cambiarCantidad,
    quitar,
    cambiarMoneda,
    cambiarDescuento,
    asignarCliente,
    elegirMostrador,
    vaciar,
    claveIdempotencia,
  } = useTicket();

  const [eligiendoCliente, setEligiendoCliente] = useState(false);
  /** La tecla con la que se abrio la busqueda, para no perderla. */
  const [textoCliente, setTextoCliente] = useState("");
  const [creandoCliente, setCreandoCliente] = useState(false);
  /** Lo que se busco en el selector antes de dar de alta: precarga el formulario. */
  const [buscadoAlta, setBuscadoAlta] = useState("");
  const crearCliente = useCrearCliente();
  /**
   * `aCuenta` solo tiene sentido con cliente: una venta al mostrador sin cliente
   * no puede ir a cuenta corriente porque no hay cuenta a la cual cargarla.
   */
  const [aCuenta, setACuenta] = useState(false);
  /*
   * La venta que se acaba de cerrar. Ocupa el ticket vacio hasta que empiece la
   * proxima: mandar el remito pasa medio minuto despues de confirmar, cuando un
   * toast ya se fue.
   */
  const [cerrada, setCerrada] = useState<VentaCerrada | null>(null);

  /**
   * La venta que la base rechazo por pasar el limite de credito, esperando que
   * quien vende diga si va igual. El limite avisa, no bloquea (lo dice la
   * ficha del cliente); antes el rechazo era un toast sin salida.
   */
  const [limitePasado, setLimitePasado] = useState<{
    credito: CreditoExcedido;
    cliente: string | null;
    venderIgual: () => void;
  } | null>(null);

  const { data: config } = useConfig();
  const { data: cuentas } = useCuentasFinancieras();
  // La moneda de su cuenta: el ticket avisa si la deuda va a quedar en otra
  // moneda que la de la venta. Es la misma consulta que abre su ficha.
  const { data: fichaCliente } = useCliente(ticket.clienteId ?? undefined);

  const navegar = useNavigate();
  // Que usados estan hoy en el tecnico: se avisa al agregarlos, no se bloquea.
  // Compartido con el buscador global, que es el otro camino hacia el ticket.
  const avisarEnTaller = useAvisoEnTaller();

  const cotizacion = Number(config?.cotizacion_vigente ?? 0);
  const venderContado = useEmitirVentaContado();
  const venderACuenta = useEmitirVenta();
  const guardando = venderContado.isPending || venderACuenta.isPending;

  /**
   * El precio sale del resultado de busqueda, que ya viene con el valor vigente
   * en las dos monedas. El backend lo vuelve a resolver al emitir, asi que si el
   * dolar cambia entre que se arma el ticket y se cobra gana el del backend —
   * pero mientras tanto el vendedor ve un numero real y no un cero.
   */
  const agregarProducto = useCallback(
    (p: ResultadoParaTicket) => {
      // Un usado sin precio se venderia al costo: se frena y se dice por que.
      if (usadoSinPrecio(p)) {
        avisarUsadoSinPrecio(p.nombre);
        return;
      }
      avisarEnTaller(p.producto_id);
      // Tambien si es a pedido y cuanto hay: el ticket avisa ANTES de cobrar
      // que la venta deja un encargo o que se vende mas de lo que hay.
      agregar(itemDeResultado(p));
    },
    [agregar, avisarEnTaller],
  );

  const cobrar = useCallback(
    (
      pagos: PagoVentaContado[],
      entrega: EntregaUsado | null,
      vueltoPropina: boolean,
      deuda: number,
      saldoAFavor: number,
    ) => {
      const items = ticket.items.map((i) => ({
        producto_id: i.producto_id,
        cantidad: i.cantidad,
        // El precio viaja SOLO si se lo edito a mano. Si no, lo resuelve el
        // backend con la lista vigente: mandar el que se vio en pantalla
        // congelaria un precio viejo si el dolar se movio mientras tanto.
        ...(i.precio_editado ? { precio_unitario: i.precio_unitario } : {}),
        ...(i.descuento_pct ? { descuento_pct: i.descuento_pct } : {}),
        ...(i.serie_imei ? { serie_imei: i.serie_imei } : {}),
        // El color que pidio el cliente para lo que se encarga.
        ...(i.nota ? { observaciones: i.nota } : {}),
      }));

      const descuento =
        ticket.descuentoTipo && ticket.descuentoValor > 0
          ? { descuento_tipo: ticket.descuentoTipo, descuento_valor: ticket.descuentoValor }
          : {};

      const alTerminar = {
        onSuccess: (r: any) => {
          // El detalle se captura ANTES de `vaciar()`: despues los items ya no
          // existen y el mensaje del remito se quedaria sin que enumerar.
          // El importe en la moneda de la VENTA y con el descuento de la linea:
          // un iPhone cotizado en dolares en una venta en pesos salia en el
          // mensaje con su precio en dolares escrito como pesos.
          const itemsRemito = ticket.items.map((i) => ({
            descripcion: i.nota ? `${i.nombre} · ${i.nota}` : i.nombre,
            cantidad: i.cantidad,
            importe:
              convertir(i.precio_unitario, i.precio_moneda, ticket.moneda, cotizacion || 1) *
              i.cantidad *
              (1 - (i.descuento_pct ?? 0) / 100),
            a_pedido: encargadosDe(i) > 0,
          }));
          // Lo que la base encargo: lo a pedido que excede el stock. Mirar
          // solo el flag callaba el aviso con stock 1 y cantidad 2.
          const encargados = ticket.items.filter((i) => encargadosDe(i) > 0).map((i) => i.nombre);
          const clienteId = ticket.clienteId;
          const clienteNombre = ticket.clienteNombre;
          const monedaVenta = ticket.moneda;

          // Como se pago, para el remito. Sale de lo que contesto el RPC y no
          // del formulario: `movimientos` trae la moneda REAL de cada caja
          // (pagar en dolares una venta en pesos es normal acá) y la entrega,
          // su valor de toma ya validado. Es la misma forma que sirve
          // `v_remito_publico_pagos` cuando el remito se reenvía después.
          const pagosRemito: LineaPagoRemito[] = [
            ...((r?.movimientos ?? []) as any[]).map((m): LineaPagoRemito => ({
              clase: "PAGO",
              etiqueta: etiquetaMedio(String(m.medio)),
              importe: aNumero(m.importe),
              moneda: m.moneda,
            })),
            ...(r?.entrega
              ? [
                  {
                    clase: "ENTREGA" as const,
                    etiqueta: String(r.entrega.nombre),
                    importe: aNumero(r.entrega.valor_toma),
                    moneda: r.entrega.moneda ?? monedaVenta,
                  },
                ]
              : []),
          ];

          vaciar();
          setACuenta(false);
          const faltantes = r?.faltantes ?? [];
          // Lo que quedo debiendo ESTE comprobante, ya imputado por el backend.
          // Es lo primero que hay que ver despues de un pago parcial: la venta
          // salio, pero la plata no entro toda.
          //
          // La venta a cuenta no devuelve `pendiente` (`emitir_venta` no lo
          // tiene) y no entra ningun pago: queda debiendo el total. Con el 0 de
          // antes el panel no avisaba nada y el WhatsApp le decia al cliente
          // «Pagaste» lo que en realidad fue a su cuenta.
          const total = Number(r?.total ?? 0);
          const pendiente = aCuenta ? total : Number(r?.pendiente ?? 0);

          // Una venta en dolares a una cuenta fija en pesos (o al reves) deja la
          // deuda en la moneda de la cuenta. El aviso y el WhatsApp decian solo
          // «US$ 12,58», y la ficha y el remito, $ 20.002,20 fijos. Misma
          // cuenta que `pendiente_cuenta` de `v_remito_publico`: el pendiente
          // a la cotizacion de la venta.
          const monedaCuenta =
            clienteId !== null && fichaCliente ? monedaAnclaje(fichaCliente.cliente) : null;
          const pendienteCuenta =
            monedaCuenta && monedaCuenta !== monedaVenta && pendiente > 0.009 && cotizacion > 0
              ? Math.round(convertir(pendiente, monedaVenta, monedaCuenta, cotizacion) * 100) / 100
              : null;
          const enSuCuenta =
            pendienteCuenta === null || monedaCuenta === null
              ? ""
              : monedaCuenta === "ARS"
                ? ` Su cuenta es en pesos: queda fijo en ${formatear(pendienteCuenta, "ARS")}.`
                : ` Su cuenta es en dólares: queda en ${formatear(pendienteCuenta, "USD")}.`;

          const saldoUsado = aNumero(r?.saldo_a_favor_usado ?? 0);
          const avisos = [
            // Lo que pago su saldo a favor no entro a ninguna caja (0121_8).
            saldoUsado > 0.009
              ? `Usó ${formatear(saldoUsado, monedaVenta)} de su saldo a favor.`
              : null,
            pendiente > 0.009
              ? `Queda debiendo ${formatear(pendiente, monedaVenta)} en su cuenta corriente.${enSuCuenta}`
              : null,
            faltantes.length > 0
              ? `Ojo: vendiste ${faltantes
                  .map((f: any) => `${f.falta} ${f.nombre}`)
                  .join(", ")} sin tener stock.`
              : null,
            encargados.length > 0
              ? `Queda como encargo: ${encargados.join(", ")} se entrega cuando llegue.`
              : null,
          ].filter((a): a is string => a !== null);

          setCerrada({
            numero: String(r?.numero ?? ""),
            total,
            pagado: Math.max(total - pendiente, 0),
            pendiente,
            moneda: monedaVenta,
            monedaCuenta,
            pendienteCuenta,
            token: (r?.token_publico as string | null) ?? null,
            items: itemsRemito,
            pagos: pagosRemito,
            clienteId,
            clienteNombre,
            avisos,
            entrega: r?.entrega
              ? { producto_id: Number(r.entrega.producto_id), nombre: String(r.entrega.nombre) }
              : null,
            // Lo que pago el saldo a favor, segun la base: el WhatsApp lo dice
            // aparte de la plata (0121_8).
            saldoAFavor: saldoUsado,
          });
        },
        onError: mostrarError,
      };

      const dejaDeuda = deuda > 0.009;
      const deudaEntraEnLimite =
        dejaDeuda &&
        !aCuenta &&
        deudaEntraEnElCredito({
          deuda,
          monedaVenta: ticket.moneda,
          ficha: fichaCliente,
          cotizacion,
        });

      // Si la base la rechaza por el limite de credito, se pregunta y se
      // reintenta el MISMO pedido con `forzar`. Cualquier otro error, al toast.
      const siPasaElLimite = (error: unknown, venderIgual: () => void): void => {
        const credito = leerCreditoExcedido(error);
        if (!credito) {
          mostrarError(error);
          return;
        }
        // En un pago parcial la base habla del total; lo que va a la cuenta es
        // la deuda, y es lo que el dialogo tiene que decir.
        const deudaEnLimite =
          dejaDeuda && !aCuenta && (credito.moneda === ticket.moneda || cotizacion > 0)
            ? Math.round(convertir(deuda, ticket.moneda, credito.moneda, cotizacion) * 100) / 100
            : undefined;
        setLimitePasado({
          credito: deudaEnLimite === undefined ? credito : { ...credito, deuda: deudaEnLimite },
          cliente: ticket.clienteNombre,
          venderIgual,
        });
      };

      // A cuenta corriente es `emitir_venta` a secas: no hay pagos, la deuda
      // queda en la cuenta del cliente y se cobra despues. Contado es
      // `emitir_venta_contado`, que ademas escribe la caja y deja el
      // comprobante cancelado, todo en una transaccion.
      if (aCuenta && ticket.clienteId !== null) {
        const venta = {
          moneda: ticket.moneda,
          cotizacion,
          cliente_id: ticket.clienteId,
          items,
          ...descuento,
        };
        venderACuenta.mutate(venta, {
          ...alTerminar,
          onError: (e) =>
            siPasaElLimite(e, () => venderACuenta.mutate({ ...venta, forzar: true }, alTerminar)),
        });
        return;
      }

      const venta = {
        moneda: ticket.moneda,
        // La cotizacion que viaja es la que estaba EN PANTALLA cuando la
        // persona apreto Cobrar, no la que resuelva el servidor al hacer el
        // commit. Entre las dos puede haber un cambio de dolar, y el cliente
        // pago el numero que vio.
        cotizacion,
        cliente_id: ticket.clienteId ?? undefined,
        items,
        ...descuento,
        pagos,
        // El equipo que dejo en parte de pago: el backend lo da de alta como
        // usado con su costo de toma y descuenta el valor de lo cobrado.
        ...(entrega ? { entrega } : {}),
        // El cliente dejo el vuelto: el backend lo asienta como propina.
        ...(vueltoPropina ? { vuelto_propina: true } : {}),
        // El saldo a favor que quien vende eligio usar (0121_8). Sin elegirlo
        // no viaja y la base no lo toca.
        ...(saldoAFavor > 0.009 ? { saldo_a_favor: saldoAFavor } : {}),
        // La llave del ticket. Si la red se corta a los 15 segundos y el
        // vendedor aprieta de nuevo, el servidor reconoce el reintento y
        // devuelve el comprobante que ya emitio en vez de emitir otro.
        clave: claveIdempotencia,
        // Pagada entera no deja deuda: el limite de credito no tiene nada que
        // decir. `emitir_venta` lo mira ANTES de que entren los pagos, y una
        // venta de contado a un cliente con limite (o ya pasado) rebotaba con
        // «excede el credito disponible» aunque pagara todo en efectivo. Lo
        // mismo un pago parcial cuya deuda entra en el limite: la base compara
        // el total, y preguntaba por un exceso que no existia.
        ...(!dejaDeuda || deudaEntraEnLimite ? { forzar: true } : {}),
      };
      venderContado.mutate(venta, {
        ...alTerminar,
        onError: (e) =>
          siPasaElLimite(e, () => venderContado.mutate({ ...venta, forzar: true }, alTerminar)),
      });
    },
    [venderContado, venderACuenta, aCuenta, ticket, cotizacion, vaciar, fichaCliente],
  );

  return (
    // `Pagina` da el gap de 24px entre el panel de la venta cerrada y el
    // ticket; `flex-col` con `h-full` es para que el ticket estire.
    <Pagina className="flex flex-col md:h-full">
      {/* ══ La venta, y nada mas ══ */}
      {cerrada ? (
        <section className="min-w-0">
          <PanelVentaCerrada
            venta={cerrada}
            negocio={config?.config?.nombre ?? "MANZANA MORDIDA"}
            onCerrar={() => setCerrada(null)}
            onReacondicionar={(id) => {
              setCerrada(null);
              void navegar({ to: "/reparaciones", search: { recibir: id } });
            }}
          />
        </section>
      ) : null}

      {/*
        El ticket no dibuja su propio borde: la tarjeta es este contenedor.

        `md:min-h-0` en vez del piso de 34rem: en desktop el shell mide una
        pantalla exacta y la tarjeta se estira a lo que sobre, ni un pixel mas.
        Un minimo fijo la empujaba fuera de la vista en cualquier notebook
        —justo la pantalla que mas se usa— y el ticket ya scrollea por dentro
        su propia lista de items, asi que el alto de la tarjeta no le hace
        perder nada. En mobile el piso queda: ahi la pagina si scrollea y sin
        un minimo la tarjeta colapsa a la altura del encabezado del ticket.
      */}
      <Card
        as="section"
        padding="none"
        // En el celular la tarjeta crece con el ticket y se desliza la pantalla
        // entera (ver «dos pasos» en TicketVenta): con alto fijo y el
        // desborde escondido, los productos vivían en una ventana de 128 px.
        className="flex min-h-[28rem] min-w-0 flex-1 flex-col max-md:flex-none md:min-h-0 md:overflow-hidden"
      >
        {/*
          El h1 se lee pero no se ve. El titulo "Mostrador" arriba de la
          tarjeta decia lo que el propio ticket ya dice tres centimetros mas
          abajo —"Mostrador · Venta sin cliente asignado"— y costaba 52px del
          alto de la pantalla que estamos tratando de ganar. Para el lector de
          pantalla el nombre de la pagina si hace falta, asi que queda aca:
          `sr-only` es `position:absolute`, o sea que no ocupa nada del flex.
        */}
        <h1 className="sr-only">Mostrador</h1>
        <TicketVenta
          items={ticket.items}
          moneda={ticket.moneda}
          cotizacion={cotizacion || 1}
          cuentas={(cuentas?.filas ?? []) as CuentaFinanciera[]}
          clienteId={ticket.clienteId}
          clienteNombre={ticket.clienteNombre}
          clienteAnclaje={fichaCliente ? monedaAnclaje(fichaCliente.cliente) : null}
          saldoAFavor={fichaCliente ? aNumero(fichaCliente.saldo_a_favor ?? 0) : null}
          guardando={guardando}
          mostrarInternos={internos}
          aCuenta={aCuenta}
          onCambiarACuenta={setACuenta}
          mostrador={ticket.mostrador}
          onElegirMostrador={() => elegirMostrador(true)}
          onElegirCliente={(texto) => {
            setTextoCliente(texto ?? "");
            setEligiendoCliente(true);
          }}
          onQuitarCliente={() => {
            asignarCliente(null, null);
            // Sin cliente no hay cuenta corriente: se vuelve a contado solo.
            setACuenta(false);
            // Con productos ya cargados, sacar el cliente NO vuelve a preguntar:
            // es cambiar la respuesta a "mostrador", no borrarla. Volver a la
            // pregunta con medio ticket armado esconderia el trabajo hecho.
            elegirMostrador(ticket.items.length > 0);
          }}
          descuentoTipo={ticket.descuentoTipo}
          descuentoValor={ticket.descuentoValor}
          onCambiarCantidad={cambiarCantidad}
          onEditarItem={editarItem}
          onQuitar={quitar}
          onCambiarMoneda={cambiarMoneda}
          onCambiarDescuento={cambiarDescuento}
          onAgregarProducto={agregarProducto}
          onCobrar={cobrar}
          onVaciar={vaciar}
          recordarCobro
        />
      </Card>

      <SelectorCliente
        abierto={eligiendoCliente}
        textoInicial={textoCliente}
        onCerrar={() => setEligiendoCliente(false)}
        onElegir={(id, nombre) => asignarCliente(id, nombre)}
        // El alta se abre ENCIMA de la venta. Antes era un link a /clientes que
        // navegaba fuera y se llevaba puesto el ticket cargado.
        onCrearNuevo={(buscado) => {
          setEligiendoCliente(false);
          setBuscadoAlta(buscado);
          setCreandoCliente(true);
        }}
      />

      {/* Mismo formulario que la seccion Clientes: un alta hecha desde la venta
          no puede pedir menos datos ni validar distinto. Al crearlo queda
          asignado al ticket solo, que es para lo que se abrio. */}
      <DialogCliente
        abierto={creandoCliente}
        onAbiertoChange={setCreandoCliente}
        cliente={null}
        inicial={buscadoAlta}
        cotizacion={cotizacion}
        guardando={crearCliente.isPending}
        onCrear={(datos) =>
          crearCliente.mutate(datos, {
            onSuccess: (cliente) => {
              setCreandoCliente(false);
              asignarCliente(cliente.id, cliente.nombre);
            },
            onError: mostrarError,
          })
        }
        onActualizar={() => undefined}
      />

      <DialogLimiteCredito
        credito={limitePasado?.credito ?? null}
        cliente={limitePasado?.cliente ?? null}
        guardando={guardando}
        onCancelar={() => setLimitePasado(null)}
        onVenderIgual={() => {
          const venderIgual = limitePasado?.venderIgual;
          setLimitePasado(null);
          venderIgual?.();
        }}
      />
    </Pagina>
  );
}
